import {
  EBeneficiaryType,
  ECreateLoanStatus,
  ECreditApplicationStatus,
  EFinancingProgram,
  ELoanPurpose,
  EOriginSystemId,
  EProgressionStatus,
} from '@if/core/types'
import { copyMessage, formatCurrency, formatDate } from '@if/core/utils'
import BlockIcon from '@mui/icons-material/Block'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import InfoIcon from '@mui/icons-material/Info'
import LanguageIcon from '@mui/icons-material/Language'
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { Badge, Box, Button, ButtonGroup, Grid, IconButton, Link, Stack, SvgIcon, Tooltip } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import LoopIcon from '@src/assets/loop.svg?react'
import {
  useGetCreateLoanStatus,
  useSendHighEstimateFundingConfirmation,
} from '@src/data/api/credit-application/credit-application-api'
import { appActions, appSelectors } from '@src/data/store/AppStore'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import LanguageProvinceIndicator from '../../../components/LanguageProvinceIndicator'
import { useAppDispatch, useAppSelector } from '../../../data/store'
import { creditEffects, creditSelectors } from '../../../data/store/CreditApplication'
import { CreditApplication, CreditApplicationMessage, User } from '../../../data/types'
import { Merchant } from '../../../data/types/Merchant'
import CopyLoanAsDraftBtn from './CopyLoanAsDraftBtn'
import EmailAddress from './EmailAddress'
import PhoneNumber from './PhoneNumber'

interface Props {
  creditApplication: CreditApplication
  creditApplicationMessage: CreditApplicationMessage[]
  merchant: Merchant
  onStatusChange: () => void
  onMerchantChanged: () => void
  creditApplicationCanBeLocked: boolean
  loanId: number | undefined | null
  user: User | null
  isAbleSendFundDepositConfirmation: boolean
  applicationStatus: EProgressionStatus
  editDisabled: boolean
  finalDecisionIsPending: boolean
  allTasksRequiredForPayoutCompleted: boolean
  allDocumentsRequiredForPayOutCompleted: boolean
  hasDocumentsPendingApproval: boolean
  ishighEstimate: boolean
  hasConfirmedHighEstimateFunding: boolean
  onBanApplicant: () => void
  incomeConfirmed: boolean
}

const ApplicationResume = ({
  creditApplication,
  creditApplicationMessage,
  merchant,
  onStatusChange,
  onMerchantChanged,
  creditApplicationCanBeLocked,
  loanId,
  user,
  isAbleSendFundDepositConfirmation,
  applicationStatus,
  editDisabled,
  finalDecisionIsPending,
  allTasksRequiredForPayoutCompleted,
  allDocumentsRequiredForPayOutCompleted,
  hasDocumentsPendingApproval,
  ishighEstimate,
  hasConfirmedHighEstimateFunding,
  onBanApplicant,
  incomeConfirmed,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  const renderCancelButtonText = useMemo(() => {
    if (creditApplication.status !== ECreditApplicationStatus.Cancelled) return t('common.cancel')
    return t('common.reactivate')
  }, [creditApplication.status, t])

  const featureFlags = useSelector(appSelectors.getFeatureFlags)

  const showAdditionalSummaryVariables = featureFlags.showAdditionalSummaryVariables
  const useBanishmentValidation = featureFlags.useBanishmentValidation
  const financingProgramConfig = useAppSelector(creditSelectors.getFinancingProgramConfigs)
  const applyOnlineLink = useAppSelector(creditSelectors.getApplyOnlineLink)
  const activateDownloadFCFButton = featureFlags.activateDownloadFCFButton

  const containsAtSymbol = (str: string): boolean => {
    return str.includes('@')
  }

  const isReadyToCreateLoan = Boolean(
    (creditApplication.financingProgramId === EFinancingProgram.AutoRsla ||
      creditApplication.financingProgramId === EFinancingProgram.CreditRsla ||
      (creditApplication.financingProgramId !== EFinancingProgram.Auto && creditApplicationCanBeLocked) ||
      creditApplication.editLocked) &&
      creditApplication.loanId == null &&
      creditApplication.cvtNumber != null &&
      creditApplication.paymentBankAccount &&
      !finalDecisionIsPending &&
      allTasksRequiredForPayoutCompleted &&
      allDocumentsRequiredForPayOutCompleted &&
      !hasDocumentsPendingApproval,
  )

  const canCreateLoan = user?.rights.canCreateLoan === true && isReadyToCreateLoan

  const dispatchEffect = useSideEffect()
  const handleLockingCreditApplication = useCallback(() => {
    if (creditApplication?.id) {
      if (!creditApplication.editLocked) {
        dispatchEffect(
          creditEffects.lockCreditApplication({
            creditApplicationId: creditApplication.id,
            financingProgramId: creditApplication.financingProgramId,
          }),
        ).catch(reportErrorToConsole)
      } else {
        dispatchEffect(
          creditEffects.unlockCreditApplication({
            creditApplicationId: creditApplication.id,
            financingProgramId: creditApplication.financingProgramId,
          }),
        ).catch(reportErrorToConsole)
      }
    }
  }, [creditApplication?.id, creditApplication?.editLocked, creditApplication.financingProgramId, dispatchEffect])

  const onHandlePayOut = useCallback(() => {
    if (creditApplication?.id) {
      dispatchEffect(
        creditEffects.payOut({
          creditApplicationId: creditApplication.id,
          financingProgramId: creditApplication.financingProgramId,
        }),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication?.id, creditApplication?.financingProgramId, dispatchEffect])

  const onHandleFundDepositConfirmation = useCallback(() => {
    if (creditApplication?.id) {
      dispatchEffect(
        creditEffects.sendFundDepositConfirmation({
          creditApplicationId: creditApplication.id,
          financingProgramId: creditApplication.financingProgramId,
        }),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication?.id, creditApplication?.financingProgramId, dispatchEffect])

  const onHandleDownloadFCF = useCallback(() => {
    if (creditApplication?.id) {
      dispatchEffect(
        creditEffects.downloadFCFDocument({
          creditApplicationId: creditApplication.id,
          financingProgramId: creditApplication.financingProgramId,
        }),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication, dispatchEffect])

  const [shouldPollCreateLoanStatus, setShouldPollCreateLoanStatus] = useState(canCreateLoan)

  const [pollCreateLoanStatus, isPollingCreateLoanStatus, refetch] = useGetCreateLoanStatus(
    creditApplication.financingProgramId,
    creditApplication.id,
    shouldPollCreateLoanStatus,
  )
  const handleHighEstimateFundingConfirmation = async () => {
    await sendHighEstimateFundingConfirmation()
    dispatchEffect(
      creditEffects.getCurrentCreditApplicationTasks(creditApplication.id, creditApplication.financingProgramId),
    ).catch(reportErrorToConsole)
  }
  const [loanLoading, setLoanLoading] = useState(false)
  const [sendHighEstimateFundingConfirmation, isSendingHighEstimateFundingConfirmation] =
    useSendHighEstimateFundingConfirmation(creditApplication.financingProgramId, creditApplication.id)

  useEffect(() => {
    if (shouldPollCreateLoanStatus && !isPollingCreateLoanStatus) {
      if (pollCreateLoanStatus?.runtimeStatus === ECreateLoanStatus.NotCreated) {
        setShouldPollCreateLoanStatus(false)
      }
      if (pollCreateLoanStatus?.runtimeStatus === ECreateLoanStatus.Failed) {
        setShouldPollCreateLoanStatus(false)
        dispatchEffect(
          creditEffects.getCurrentCreditApplicationTasks(creditApplication.id, creditApplication.financingProgramId),
        ).catch(reportErrorToConsole)
      }

      if (pollCreateLoanStatus?.runtimeStatus === ECreateLoanStatus.Completed) {
        setShouldPollCreateLoanStatus(false)
        dispatchEffect(creditEffects.getById(creditApplication.id, creditApplication.financingProgramId)).catch(
          reportErrorToConsole,
        )
      }
      if (pollCreateLoanStatus?.runtimeStatus === ECreateLoanStatus.Completed) {
        setShouldPollCreateLoanStatus(false)
        dispatchEffect(creditEffects.getById(creditApplication.id, creditApplication.financingProgramId)).catch(
          reportErrorToConsole,
        )
      }
    }
  }, [
    shouldPollCreateLoanStatus,
    isPollingCreateLoanStatus,
    pollCreateLoanStatus?.runtimeStatus,
    dispatchEffect,
    creditApplication.id,
    creditApplication.financingProgramId,
  ])
  const handleCreateLoan = useCallback(() => {
    if (!creditApplication?.loanId) {
      if (!loanLoading) {
        setLoanLoading(true)
        dispatchEffect(
          creditEffects.createLoan({
            financingProgramId: creditApplication.financingProgramId,
            id: creditApplication.id,
          }),
        )
          .catch((handleLoanError) => {
            reportErrorToConsole(handleLoanError)
            if (!financingProgramConfig.useCreateAlisLoan) {
              dispatch(appActions.setBusinessErrors([{ message: 'editCreditApplication.errors.createLoan' }]))
            }
          })
          .finally(() => {
            setShouldPollCreateLoanStatus(true)
            refetch().catch(reportErrorToConsole)
            setLoanLoading(false)
          })
      }
    }
  }, [
    creditApplication?.loanId,
    creditApplication?.financingProgramId,
    creditApplication?.id,
    dispatchEffect,
    loanLoading,
    dispatch,
    refetch,
    financingProgramConfig.useCreateAlisLoan,
  ])
  const handleViewLoan = useCallback(() => {
    window.open(
      `${import.meta.env.VITE_MONOLITH_API_URL}/admin/ppa/customer.aspx?id=${creditApplication?.loanId}`,
      '_blank',
    )
  }, [creditApplication?.loanId])

  const applicationIsDraft = creditApplication.status === ECreditApplicationStatus.Draft

  function getBeneficiaryName() {
    const {
      beneficiaryTypeId,
      applicant,
      coApplicant,
      otherBeneficiaryFirstName,
      otherBeneficiaryLastName,
      loanPurposeId,
    } = creditApplication

    switch (beneficiaryTypeId) {
      case EBeneficiaryType.Applicant:
        return `${applicant?.firstName} ${applicant?.lastName}`
      case EBeneficiaryType.Coapplicant:
        return `${coApplicant?.firstName} ${coApplicant?.lastName}`
      case EBeneficiaryType.Other:
        if (loanPurposeId === ELoanPurpose.Veterinary) {
          return `${otherBeneficiaryLastName}`
        }
        if (otherBeneficiaryFirstName === null || otherBeneficiaryLastName === null) {
          return ''
        }
        return `${otherBeneficiaryFirstName} ${otherBeneficiaryLastName}`
      default:
        return ''
    }
  }
  const isRush = creditApplication?.isRush === true
  const isLoadingRush = useAppSelector(creditSelectors.isTogglingRush)

  const handleToggleRush = () => {
    if (creditApplication && creditApplication.versionTag) {
      dispatchEffect(
        creditEffects.changeRush({
          financingProgramId: creditApplication.financingProgramId,
          creditApplicationId: creditApplication.id,
          isRush: !isRush,
          versionTag: creditApplication.versionTag,
        }),
      ).catch(reportErrorToConsole)
    }
  }
  const isMerchandStateIso =
    creditApplication.financingProgramId === EFinancingProgram.Auto ||
    creditApplication.originSystemId === EOriginSystemId.MerchantDashboard

  return (
    <Grid container spacing={2} sx={{ mb: 2 }} size={12}>
      <Grid
        size={{
          xs: 12,
          md: 12,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <span>
            <Typography variant="h6" component="div">
              {t('common.application')} : #{creditApplication.referenceNumber}
              {creditApplication?.parentCreditApplicationId && (
                <>
                  {' ('}
                  {t('credit.refinancing')}{' '}
                  <Link
                    rel="noopener noreferrer"
                    target="_blank"
                    href={`/Applications/${creditApplication.financingProgramId}/${creditApplication.parentCreditApplicationId}/view`}
                  >
                    {creditApplication.parentReferenceNumber}
                  </Link>
                  {' )'}
                </>
              )}
            </Typography>

            {financingProgramConfig.useCreateAlisLoan && creditApplication.loanId && (
              <Typography variant="h6" component="div">
                {t('common.loan')} : #{creditApplication.loanId}
              </Typography>
            )}
            {creditApplication.financingProgramId === EFinancingProgram.Auto && (
              <Typography variant="h6" component="div" gutterBottom>
                #DT: #TODO
              </Typography>
            )}
          </span>
          {creditApplication.financingProgramId === EFinancingProgram.Auto && (
            <IconButton disabled={applicationIsDraft} aria-label="Ice-center" color="primary">
              <LanguageIcon fontSize="large" />
            </IconButton>
          )}
          <Typography variant="body1" component="div" sx={{ border: '1px solid', borderRadius: 2, p: 1 }}>
            {t('editCreditApplication.beneficiary.beneficiary')}: {getBeneficiaryName()}
          </Typography>
          <Typography variant="body1" component="div" sx={{ border: '1px solid', borderRadius: 2, p: 1 }}>
            {t('common.program')} : {t(`EFinancingProgram.${creditApplication.financingProgramId}`, { ns: 'enum' })}
          </Typography>
          {creditApplication.financingProgramId === EFinancingProgram.Auto && (
            <Badge badgeContent={creditApplicationMessage.length} color="success">
              <Button
                variant="outlined"
                size="large"
                onClick={() =>
                  openInNewTab(`/Applications/${creditApplication.financingProgramId}/${creditApplication.id}/messages`)
                }
              >
                {t('message.message', '', { count: creditApplicationMessage.length })}
              </Button>
            </Badge>
          )}
          {creditApplication.financingProgramId === EFinancingProgram.Auto && (
            <Badge color="success">
              <Button
                variant="outlined"
                size="large"
                disabled={
                  !(creditApplicationCanBeLocked && user?.rights.canEditCreditApp && user?.isInternal && !editDisabled)
                }
                onClick={handleLockingCreditApplication}
              >
                {creditApplication.editLocked ? t('lockCredit.unlockCredit') : t('lockCredit.lockCredit')}
              </Button>
            </Badge>
          )}

          <div>
            {isMerchandStateIso && <StorefrontIcon fontSize="large" color="primary" />}

            <LanguageProvinceIndicator
              correspondanceLanguageId={creditApplication.correspondanceLanguageId}
              applicantStateIso={
                isMerchandStateIso ? merchant.province : (creditApplication.applicant.currentAddress.stateIso ?? null)
              }
            />
          </div>
        </Stack>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 8,
        }}
      >
        <Box sx={{ border: '1px solid grey', borderRadius: 2, p: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <span>
              <Typography>
                {t('common.origin')} : {t(`EOriginSystemId.${creditApplication.originSystemId}`, { ns: 'enum' })}
              </Typography>
              {creditApplication.financingProgramId === EFinancingProgram.Personal && (
                <Typography variant="body1" component="div">
                  {t('editCreditApplication.loanPurpose')} :{' '}
                  {t(`ELoanPurpose.${creditApplication.loanPurposeId}`, { ns: 'enum' })}
                </Typography>
              )}
              <Typography variant="body1" component="div">
                {t('common.status')} :{' '}
                {applicationIsDraft
                  ? t('ECreditApplicationStatus.draft', { ns: 'enum' })
                  : t(`EProgressionStatusAbbr.${applicationStatus}`, { ns: 'enum' })}
              </Typography>
              <Typography variant="body1" component="div" gutterBottom>
                {t('common.createdOn')} : {formatDate(creditApplication.createdOn)}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {t('common.expiresOn')} : {formatDate(creditApplication.expiresOn)}
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t('expirationToolTipe.title')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('expirationToolTipe.expiresOn')}{' '}
                        {new Date(creditApplication.expiresOn).toLocaleString('en-CA', { timeZone: 'UTC' })}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('expirationToolTipe.currentTime')} {new Date().toLocaleString('en-CA', { timeZone: 'UTC' })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('expirationToolTipe.expireRunAt')}
                      </Typography>
                    </Box>
                  }
                  placement="right"
                  describeChild
                >
                  <IconButton size="small" sx={{ padding: 0 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </span>
            <span>
              {financingProgramConfig.shouldSeeAmounts && (
                <div>
                  <Typography variant="body1" component="div">
                    {t('editCreditApplication.amountRequested')} :{' '}
                    {formatCurrency(creditApplication.initialRequestedLoanAmount ?? 0)}
                  </Typography>
                  <Typography variant="body1" component="div">
                    {t('editCreditApplication.amountGranted')} :{' '}
                    {formatCurrency(creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0)}
                  </Typography>
                  <Typography variant="body1" component="div">
                    {t('editCreditApplication.maximumLoanAmount')} :{' '}
                    {formatCurrency(creditApplication?.finalCreditDecision?.maximumAvalaibleAmount ?? 0)}
                  </Typography>
                </div>
              )}
              <Typography variant="body1" component="div">
                {t('common.merchant')} : {merchant?.name}
              </Typography>
              {merchant && (
                <Typography variant="body1" component="div">
                  {t('editCreditApplication.merchantReferenceCode')} : {creditApplication?.merchantReferenceCode ?? ''}
                </Typography>
              )}
              <Typography variant="body1" component="div" gutterBottom>
                {t('common.by')} : {creditApplication.createdByUserFullname}
              </Typography>
              {showAdditionalSummaryVariables && containsAtSymbol(creditApplication.createdByUserId) && (
                <Typography variant="body1" component="div">
                  <EmailAddress email={creditApplication.createdByUserId} />
                </Typography>
              )}
              <PhoneNumber phone={merchant?.phone} applyPhoneFormatting={false} contacts={merchant.contacts} />
            </span>
            <span>
              <span>
                <IconButton
                  aria-label="toggle-rush"
                  onClick={handleToggleRush}
                  disabled={!user?.rights.canManageRushTag || isLoadingRush}
                >
                  {isLoadingRush ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SvgIcon fontSize="large" style={{ color: isRush ? 'red' : undefined }}>
                      <InfoIcon fontSize="small" />
                    </SvgIcon>
                  )}
                </IconButton>
              </span>
              {applyOnlineLink && (
                <IconButton color="success" onClick={() => copyMessage(applyOnlineLink)}>
                  <SvgIcon fontSize="large">
                    <ContentCopyIcon />
                  </SvgIcon>
                </IconButton>
              )}
              <IconButton
                aria-label="swap"
                onClick={onMerchantChanged}
                disabled={!user?.rights.canChangeMerchant || editDisabled}
              >
                <SvgIcon fontSize="large">
                  <LoopIcon />
                </SvgIcon>
              </IconButton>
            </span>
          </Stack>
        </Box>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <Grid size="grow">
          <Stack direction="column" justifyContent="space-between" height="100%" spacing={1}>
            <div>
              {creditApplication.financingProgramId === EFinancingProgram.Auto && (
                <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
                  <Button
                    fullWidth
                    size="large"
                    onClick={onHandlePayOut}
                    disabled={!user?.rights.canSetDisbursed || applicationIsDraft || editDisabled}
                  >
                    {t('common.funding')}
                  </Button>
                  {!activateDownloadFCFButton && (
                    <Button disabled={applicationIsDraft} size="small">
                      <LocalPrintshopIcon fontSize="large" />
                    </Button>
                  )}
                </ButtonGroup>
              )}
              {creditApplication.financingProgramId === EFinancingProgram.Personal && (
                <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
                  <Button
                    fullWidth
                    size="large"
                    onClick={onHandleFundDepositConfirmation}
                    disabled={!(user?.rights.canSetDisbursed && isAbleSendFundDepositConfirmation && !editDisabled)}
                  >
                    {t('common.funding')}
                  </Button>
                  {!activateDownloadFCFButton && (
                    <Button disabled={applicationIsDraft} size="small">
                      <LocalPrintshopIcon fontSize="large" />
                    </Button>
                  )}
                </ButtonGroup>
              )}
              <>
                {activateDownloadFCFButton && creditApplication.financingProgramId === EFinancingProgram.Personal && (
                  <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
                    <Button
                      fullWidth
                      size="large"
                      onClick={() => {
                        onHandleDownloadFCF()
                      }}
                      disabled={!incomeConfirmed}
                    >
                      {t('editCreditApplication.FinancingConfirmationForm.btn')}
                    </Button>

                    <Button disabled={!incomeConfirmed} size="small">
                      <LocalPrintshopIcon fontSize="large" />
                    </Button>
                  </ButtonGroup>
                )}
              </>
            </div>
            <CopyLoanAsDraftBtn
              creditApplicationId={creditApplication.id}
              financingProgramId={creditApplication.financingProgramId}
            />
          </Stack>
        </Grid>

        <Grid size="grow">
          <Stack direction="column" justifyContent="space-between" alignItems="flex-center" height="100%" spacing={1}>
            {!loanId && (
              <Button
                variant="outlined"
                size="large"
                disabled={!(canCreateLoan && !editDisabled && !loanLoading && !shouldPollCreateLoanStatus)}
                onClick={handleCreateLoan}
              >
                {t('common.createLoan')}
                {(loanLoading || shouldPollCreateLoanStatus) && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </Button>
            )}
            {loanId && (
              <Button
                variant="outlined"
                size="large"
                disabled={financingProgramConfig.useCreateAlisLoan}
                onClick={handleViewLoan}
              >
                {t('common.consultLoan')}
              </Button>
            )}
            <Button
              variant="outlined"
              size="large"
              onClick={onStatusChange}
              disabled={!(user?.rights.canEditCreditApp && !editDisabled)}
            >
              {renderCancelButtonText}
            </Button>
            {user?.rights.canBanApplicant && useBanishmentValidation && (
              <Button
                variant="outlined"
                size="large"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => {
                  onBanApplicant()
                }}
                disabled={editDisabled || creditApplication.finalCreditDecision?.refusalReasonCodes?.includes('Banned')}
                fullWidth
              >
                {t('ban.banButtonLabel')}
              </Button>
            )}
          </Stack>
        </Grid>
        {ishighEstimate && (
          <Button
            fullWidth
            size="large"
            variant="outlined"
            onClick={handleHighEstimateFundingConfirmation}
            disabled={isSendingHighEstimateFundingConfirmation}
          >
            {hasConfirmedHighEstimateFunding
              ? t('common.ResendHighEstimateConfirmFunding')
              : t('common.HighEstimateConfirmFunding')}
          </Button>
        )}
      </Grid>
    </Grid>
  )
}

export default ApplicationResume
